<template>
  <div class="folder-picker-redesigned">
    <div class="folder-picker-container">
      <!-- Left Panel: Case Search and Selection -->
      <div class="folder-picker-panel folder-picker-panel--search">
        <div class="panel-header">
          <div class="panel-header-icon">
            <v-icon size="20">mdi-magnify</v-icon>
          </div>
          <h3 class="panel-title">{{ $t('components.general.file_management.folder_picker.search_cases') }}</h3>
        </div>

        <div class="panel-content">
          <!-- Search Input -->
          <div class="search-input-wrapper">
            <v-text-field
              class="folder-picker-search-input"
              type="text"
              :placeholder="$t('components.general.file_management.folder_picker.search_placeholder')"
              v-model="search_txt"
              outlined
              dense
              hide-details
              :loading="searching"
              :error="!!searchError"
              prepend-inner-icon="mdi-briefcase-search-outline"
            >
              <template v-slot:append>
                <v-icon
                  v-if="search_txt && !searching"
                  @click="search_txt = ''"
                  class="clear-search-icon"
                  size="18"
                >
                  mdi-close-circle
                </v-icon>
              </template>
            </v-text-field>
            <transition name="fade">
              <span v-if="searchError" class="search-error-hint">{{ searchError }}</span>
            </transition>
          </div>

          <!-- Case Dropdown -->
          <div class="case-dropdown-section">
            <label class="dropdown-label">
              {{ $t('components.general.file_management.folder_picker.selected_case') }}
            </label>
            <div class="dropdown-wrapper">
              <CustomDropdown
                :id="'drop_cases'"
                :additional_classes="['folder-picker-dropdown']"
                v-model="selected_group_id"
                :drop_items="dropp_elements"
                :init_text="dropdownInitText"
              />
            </div>
          </div>

          <!-- Selected Case Card -->
          <transition name="slide-fade">
            <div v-if="selected_group" class="selected-case-card">
              <div class="case-card-content">
                <div class="case-card-icon">
                  <v-icon size="24" color="#372EAA">mdi-briefcase-check</v-icon>
                </div>
                <div class="case-card-info">
                  <span class="case-card-label">{{ $t('components.general.file_management.folder_picker.active_case') }}</span>
                  <span class="case-card-value">{{ selected_group.broj_predmeta }}</span>
                </div>
              </div>
              <button
                class="case-card-clear"
                @click="clearEverything()"
                :title="$t('components.general.file_management.folder_picker.clear_selection')"
              >
                <v-icon size="18">mdi-close</v-icon>
              </button>
            </div>
          </transition>

          <!-- Quick Tip -->
          <div v-if="!selected_group && results.length === 0 && !searching" class="search-tip">
            <v-icon size="16" color="#7b7b8a">mdi-lightbulb-outline</v-icon>
            <span>{{ $t('components.general.file_management.folder_picker.search_tip') }}</span>
          </div>
        </div>
      </div>

      <!-- Right Panel: Folder Selection -->
      <div class="folder-picker-panel folder-picker-panel--folders">
        <div class="panel-header">
          <div class="panel-header-icon">
            <v-icon size="20">mdi-folder-multiple-outline</v-icon>
          </div>
          <h3 class="panel-title">{{ $t('components.general.file_management.folder_picker.select_folder') }}</h3>
        </div>

        <div class="panel-content">
          <!-- Folder Filter Tags -->
          <div class="folder-filter-section">
            <folder-filter-tags
              :filter="1"
              @changedValue="(val) => selected_folder_filter = val"
            />
          </div>

          <!-- Folder Content Area -->
          <div class="folder-content-area">
            <!-- Loading State -->
            <transition name="fade" mode="out-in">
              <div v-if="loadingFolders" class="folder-state folder-state--loading" key="loading">
                <div class="loading-spinner">
                  <svg class="spinner-svg" viewBox="0 0 50 50">
                    <circle
                      class="spinner-path"
                      cx="25"
                      cy="25"
                      r="20"
                      fill="none"
                      stroke-width="4"
                    ></circle>
                  </svg>
                </div>
                <span class="state-text">{{ $t('components.general.file_management.folder_picker.loading_folders') }}</span>
              </div>

              <!-- Error State -->
              <div v-else-if="folderError" class="folder-state folder-state--error" key="error">
                <div class="error-icon-wrapper">
                  <v-icon size="40" color="#c62828">mdi-alert-circle-outline</v-icon>
                </div>
                <span class="state-text state-text--error">{{ folderError }}</span>
                <button class="retry-btn" @click="getFolderStructure()">
                  <v-icon size="16" left>mdi-refresh</v-icon>
                  {{ $t('components.general.file_management.folder_picker.retry') }}
                </button>
              </div>

              <!-- Folder Tree -->
              <div v-else-if="all_folders.length > 0" class="folder-tree-container" key="folders">
                <div class="folder-tree-scroll">
                  <div v-for="(sp, key) in all_folders" :key="key" class="folder-tree-item">
                    <tree-folders
                      :depth="0"
                      :item="sp"
                      :accessToken="accessToken"
                      @node-click="setFolderOrigin"
                      :smjer="true"
                    ></tree-folders>
                  </div>
                </div>
              </div>

              <!-- Empty State: No Case Selected -->
              <div v-else-if="!selected_group" class="folder-state folder-state--empty" key="no-case">
                <div class="empty-illustration">
                  <svg class="empty-svg" viewBox="0 0 120 100" fill="none">
                    <rect x="20" y="30" width="80" height="60" rx="4" fill="#f4f5f9" stroke="#e3e5ee" stroke-width="2"/>
                    <rect x="20" y="20" width="40" height="15" rx="3" fill="#e3e5ee"/>
                    <circle cx="95" cy="25" r="15" fill="#372EAA" opacity="0.1"/>
                    <path d="M90 25L95 30L100 20" stroke="#372EAA" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    <circle cx="60" cy="60" r="8" fill="#e3e5ee"/>
                    <path d="M57 60L60 63L63 57" stroke="#7b7b8a" stroke-width="1.5" stroke-linecap="round"/>
                  </svg>
                </div>
                <span class="state-title">{{ $t('components.general.file_management.folder_picker.no_case_selected') }}</span>
                <span class="state-subtitle">{{ $t('components.general.file_management.folder_picker.select_case_hint') }}</span>
              </div>

              <!-- Empty State: No Folders Found -->
              <div v-else class="folder-state folder-state--empty" key="no-folders">
                <div class="empty-illustration">
                  <svg class="empty-svg" viewBox="0 0 120 100" fill="none">
                    <rect x="20" y="30" width="80" height="60" rx="4" fill="#f4f5f9" stroke="#e3e5ee" stroke-width="2" stroke-dasharray="4 4"/>
                    <rect x="20" y="20" width="40" height="15" rx="3" fill="#e3e5ee"/>
                    <line x1="40" y1="50" x2="80" y2="80" stroke="#c4c4d4" stroke-width="2" stroke-linecap="round"/>
                    <line x1="80" y1="50" x2="40" y2="80" stroke="#c4c4d4" stroke-width="2" stroke-linecap="round"/>
                  </svg>
                </div>
                <span class="state-title">{{ $t('components.general.file_management.folder_picker.no_folders_found') }}</span>
                <span class="state-subtitle">{{ $t('components.general.file_management.folder_picker.no_folders_hint') }}</span>
              </div>
            </transition>
          </div>

          <!-- Selected Folder Indicator -->
          <transition name="slide-up">
            <div v-if="selected_folder" class="selected-folder-indicator">
              <v-icon size="18" color="#0ACAB1">mdi-folder-check</v-icon>
              <span class="selected-folder-name">{{ selected_folder.name }}</span>
            </div>
          </transition>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import graph from "@/services/graph.js";
import CustomDropdown from "../CustomDropdown.vue";
import predmet_caller from "../../../apiCallers/promise_handler_db/predmeti";
import TreeFolders from "../TreeFolders.vue";
import $ from "jquery";
import { DEFAULT_CASE_FOLDERS } from '../../../helpers/default_vals/enums';
import FolderFilterTags from '../../fileManagement/FolderFilterTags.vue';

export default {
  components: { CustomDropdown, TreeFolders, FolderFilterTags },
  data() {
    return {
      // Selection state
      selected_group: null,
      selected_group_id: null,
      selected_folder: null,
      selected_folder_filter: DEFAULT_CASE_FOLDERS[0],

      // Search state
      search_txt: "",
      searching: false,
      searchError: null,
      results: [],
      dropp_elements: [],
      timeout: null,

      // Folder state
      all_folders: [],
      loadingFolders: false,
      folderError: null,
    };
  },

  computed: {
    dropdownInitText() {
      if (this.searching) {
        return this.$t('components.general.file_management.folder_picker.searching');
      }
      if (this.results.length > 0) {
        return this.$t('components.general.file_management.folder_picker.select_case');
      }
      return this.$t('components.general.file_management.folder_picker.no_cases');
    }
  },

  async created() {
    const loggedInUser = this.$store.state.logged_in_user;
    const caseActivity = loggedInUser && loggedInUser.case_activity;
    if (caseActivity && caseActivity.length > 0 && caseActivity[0] && caseActivity[0].predmet && caseActivity[0].predmet.broj_predmeta) {
      this.search_txt = caseActivity[0].predmet.broj_predmeta;
    }
  },

  methods: {
    applyDialogStyling() {
      this.$nextTick(() => {
        const element = document.getElementById("fw-file-selector");
        if (!element || !element.parentElement) return;

        $(element.parentElement.parentElement).addClass("file-upload-dialog-wrapper");
        $(element.parentElement).addClass("main-file-upload-wrapper");
        $(".spis-megawrapper").removeClass("active");
        $(".main-file-upload-wrapper").find(".spis-megawrapper:first").click();
      });
    },

    setFolderOrigin(selected_folder) {
      this.selected_folder = selected_folder;
      this.$emit("foldeSelected", {
        folder_id: selected_folder.id,
        case_id: this.selected_group.group_url,
      });
    },

    clearEverything() {
      clearTimeout(this.timeout);
      this.selected_group = null;
      this.selected_group_id = null;
      this.all_folders = [];
      this.folderError = null;
    },

    async searchCases() {
      if (!this.search_txt.trim()) {
        this.results = [];
        this.dropp_elements = [];
        return;
      }

      this.searching = true;
      this.searchError = null;

      try {
        const response = await predmet_caller.searchPredmeti(this.search_txt);
        this.results = response.data.res_predmeti || [];

        if (this.results.length > 0) {
          this.dropp_elements = this.results.map((p) => ({
            id: p.group_url,
            naziv: p.broj_predmeta,
          }));
          this.selected_group_id = this.dropp_elements[0].id;
        } else {
          this.dropp_elements = [];
        }
      } catch (error) {
        console.error("Error searching cases:", error);
        this.searchError = this.$t('components.general.file_management.folder_picker.search_error');
        this.results = [];
        this.dropp_elements = [];
      } finally {
        this.searching = false;
      }
    },

    async getRootFolder(id) {
      const folder = await graph.getSingleFile(this.selected_group.group_url, id);
      return {
        item: folder,
        children: [],
      };
    },

    async fetchChildFolders(id) {
      const children = [];
      const response = await graph.getFileChildren(
        this.selected_group.group_url,
        id
      );

      if (response.value && response.value.length > 0) {
        for (const item of response.value) {
          if (item.folder) {
            const folderItem = {
              item: item,
              children: await this.fetchChildFolders(item.id),
            };
            children.push(folderItem);
          }
        }
      }
      return children;
    },

    listActiveByFilterFolders() {
      const folders = [];
      const filterName = this.selected_folder_filter && this.selected_folder_filter.name;
      switch (filterName) {
        case "spisi":
          if (this.selected_group.spis) {
            folders.push(...this.selected_group.spis.map((p) => p.id_spis));
          }
          break;
        case "radni":
          if (this.selected_group.root_radni) folders.push(this.selected_group.root_radni);
          if (this.selected_group.root_popratni) folders.push(this.selected_group.root_popratni);
          break;
        case "komunikacija":
          if (this.selected_group.root_ulazna) folders.push(this.selected_group.root_ulazna);
          if (this.selected_group.root_izlazna) folders.push(this.selected_group.root_izlazna);
          break;
      }
      return folders.filter(Boolean);
    },

    async getFolderStructure() {
      if (!this.selected_group) {
        this.all_folders = [];
        return;
      }

      this.loadingFolders = true;
      this.folderError = null;
      this.all_folders = [];

      try {
        const folderIds = this.listActiveByFilterFolders();

        if (folderIds.length === 0) {
          this.loadingFolders = false;
          return;
        }

        // Fetch all root folders in parallel
        const rootFoldersPromises = folderIds.map((folderId) =>
          this.getRootFolder(folderId)
        );
        const rootFolders = await Promise.all(rootFoldersPromises);

        // Fetch all child folders in parallel
        const childFoldersPromises = folderIds.map((folderId) =>
          this.fetchChildFolders(folderId)
        );
        const childFolders = await Promise.all(childFoldersPromises);

        // Combine root folders with their children
        this.all_folders = rootFolders.map((folder, index) => ({
          ...folder,
          children: childFolders[index],
        }));

        this.applyDialogStyling();
      } catch (error) {
        console.error("Error fetching folder structure:", error);
        this.folderError = this.$t('components.general.file_management.folder_picker.folder_error');
        this.all_folders = [];
      } finally {
        this.loadingFolders = false;
      }
    },
  },

  watch: {
    selected_folder_filter() {
      if (this.selected_group_id) {
        this.getFolderStructure();
      }
    },

    search_txt() {
      clearTimeout(this.timeout);
      this.timeout = setTimeout(() => {
        this.searchCases();
      }, 500);
    },

    selected_group_id() {
      if (this.selected_group_id && this.selected_group_id !== 0) {
        this.selected_group = this.results.find(
          (p) => p.group_url === this.selected_group_id
        );
        if (this.selected_group) {
          this.getFolderStructure();
        }
      } else {
        this.all_folders = [];
        this.selected_group = null;
        this.folderError = null;
      }
    },
  },
};
</script>

<style></style>
